import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import { invoiceCostLKR } from '../../utils/costPax';

function calcCostAndGP(inv) {
  const sell = (inv.line_items || []).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const cost = invoiceCostLKR(inv);
  return { sell, cost, gp: sell - cost };
}

export default function CostingPending() {
  const navigate = useNavigate();
  const { role, canSee } = useAuth();
  const canSeeGP = role === 'sadmin' || role === 'admin' || canSee('gp_margins');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeTab, setTypeTab] = useState('all');

  useEffect(() => {
    supabase.from('invoices')
      .select('id, invoice_number, issued_date, status, source_type, line_items, cost_items, clients(full_name, client_code)')
      .neq('status', 'VOID')
      .eq('costing_finalized', false)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setRows(data || []); setLoading(false); });
  }, []);

  const TABS = [
    ['all', 'All'],
    ['ticket', 'Tickets'],
    ['visa', 'Visas'],
    ['booking', 'Bookings'],
  ];
  const tabCounts = TABS.reduce((acc, [key]) => {
    acc[key] = key === 'all' ? rows.length : rows.filter(r => r.source_type === key).length;
    return acc;
  }, {});

  const filtered = rows.filter(inv => {
    if (typeTab !== 'all' && inv.source_type !== typeTab) return false;
    if (!q) return true;
    const qL = q.toLowerCase();
    return (
      inv.invoice_number?.toLowerCase().includes(qL) ||
      inv.clients?.full_name?.toLowerCase().includes(qL) ||
      inv.clients?.client_code?.toLowerCase().includes(qL) ||
      inv.source_type?.toLowerCase().includes(qL)
    );
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Costing Pending</div>
          <div className="page-subtitle">Invoices not yet finalized — costs can be added in stages, then click "Final Cost" to close it out</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {TABS.map(([key, label]) => (
          <button key={key} className={`btn btn-sm ${typeTab === key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTypeTab(key)}>
            {label} ({tabCounts[key]})
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="Search invoice #, client, type…" value={q}
          onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="spinner" style={{ padding: 24 }} /> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Package / Description</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Cost Status</th>
                  {canSeeGP && <th style={{ textAlign: 'right' }}>Cost</th>}
                  {canSeeGP && <th style={{ textAlign: 'right' }}>GP</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const hasCost = inv.cost_items && inv.cost_items.length > 0;
                  const { cost, gp } = calcCostAndGP(inv);
                  return (
                    <tr key={inv.id} style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/invoices/${inv.id}/costing`)}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }}>{inv.invoice_number}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{inv.clients?.full_name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inv.clients?.client_code}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>{inv.line_items?.[0]?.description || '—'}</td>
                      <td style={{ textTransform: 'capitalize', fontSize: 13, color: 'var(--text-muted)' }}>{inv.source_type || '—'}</td>
                      <td style={{ fontSize: 13 }}>{inv.issued_date || '—'}</td>
                      <td>
                        <span className={`badge ${hasCost ? 'badge-yellow' : 'badge-gray'}`}>
                          {hasCost ? `Partial (${inv.cost_items.length} supplier${inv.cost_items.length > 1 ? 's' : ''})` : 'No cost yet'}
                        </span>
                      </td>
                      {canSeeGP && (
                        <td style={{ textAlign: 'right', fontSize: 13 }}>
                          {hasCost ? cost.toLocaleString('en-US', { maximumFractionDigits: 0 }) : <span style={{ color: '#aaa' }}>—</span>}
                        </td>
                      )}
                      {canSeeGP && (
                        <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 13, color: hasCost ? (gp >= 0 ? 'var(--success)' : 'var(--danger)') : '#aaa' }}>
                          {hasCost ? gp.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—'}
                        </td>
                      )}
                      <td>
                        <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.id}/costing`); }}>
                          {hasCost ? 'Add / Finalize' : 'Add Costing'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center' }}>
                    <div className="empty-state">
                      <div className="empty-state-title" style={{ color: 'var(--success, #22c55e)' }}>{q ? 'No invoices match your search' : 'All invoices have costing'}</div>
                      <div className="empty-state-subtitle">{q ? '' : 'No pending items'}</div>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
